import { createSelector } from '@reduxjs/toolkit';
import { Analytics, DataStatus, Monitoring } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import * as SessionAPI from '../api/sessionAPI';
import type { LoginParams } from '../api/types';
import type { Profile, ViewsRestrictions } from './types';
import { OrganizationRole } from './types';

interface State {
  profile?: Profile;
  status: DataStatus;
  showPaymentOvedueBanner: boolean;
}

const initialState: State = {
  status: DataStatus.idle,
  showPaymentOvedueBanner: false,
};

export const sessionSlice = createAppSlice({
  name: 'session',
  initialState,
  reducers: (create) => ({
    setupProfile: create.asyncThunk(
      (_: void) => {
        return SessionAPI.getProfile();
      },
      {
        pending: (state) => {
          state.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.profile = action.payload;

          Monitoring.setUser({
            id: action.payload.id,
            email: action.payload.email,
          });
          Analytics.setUserId(action.payload.analyticsId);

          if (action.payload.organization.paymentOverdue) {
            state.showPaymentOvedueBanner = true;
          }
        },
        settled: (state) => {
          state.status = DataStatus.finished;
        },
      },
    ),
    login: create.asyncThunk(
      (params: LoginParams, { dispatch }) => {
        if (params.shouldRemember) {
          SessionAPI.updateRememberMe(
            (params.credentials as { email: string }).email,
          );
        }

        return SessionAPI.login(params.credentials).then(async () => {
          await dispatch(setupProfile());
        });
      },
      {
        pending: (state) => {
          state.status = DataStatus.loading;
        },
        settled: (state) => {
          state.status = DataStatus.finished;
        },
      },
    ),
    logout: create.asyncThunk(
      (_: void) => {
        SessionAPI.deleteRememberMe();
        SessionAPI.deleteShowPaymentOverdueBanner();

        return SessionAPI.logout();
      },
      {
        pending: (state) => {
          state.status = DataStatus.loading;
        },
        fulfilled: (state) => {
          state.profile = undefined;
        },
        settled: (state) => {
          state.status = DataStatus.idle;
        },
      },
    ),
    hidePaymentOverdueBanner: create.reducer((state) => {
      state.showPaymentOvedueBanner = false;
    }),
    updateViewsRestrictions: create.reducer<ViewsRestrictions>(
      (state, action) => {
        if (!state.profile) {
          return;
        }

        state.profile = {
          ...state.profile,
          viewsRestrictions: action.payload,
        };
      },
    ),
  }),
  selectors: {
    selectIsSessionRequested: (session) => {
      return session.status !== DataStatus.idle;
    },
    selectIsSessionLoading: (session) => {
      return session.status === DataStatus.loading;
    },
    selectIsAuthorized: (session) => {
      return session.status === DataStatus.finished && !!session.profile;
    },
    selectIsDisabled: (session) => {
      return !!session.profile && !session.profile.isActive;
    },
    selectIsAdmin: (session) => {
      return session.profile?.role === OrganizationRole.ADMIN;
    },
    selectUserId: (session) => {
      return session.profile?.id;
    },
    selectEmail: (session) => {
      return session.profile?.email;
    },
    selectShowPaymentOverdueBanner: (session) => {
      return session.showPaymentOvedueBanner;
    },
    selectOrganizationId: (session) => {
      return session.profile?.organization?.id;
    },
    selectWebhookHash: (session) => {
      return session.profile?.organization?.webhookHash;
    },
    selectCurrency: createSelector(
      (state) => state.profile?.organization?.currency,
      (currency) => {
        const validCurrency = isValidCurrency(currency) ? currency! : 'USD';

        return validCurrency;
      },
    ),
    selectViewsRestrictions: (session) => {
      return session.profile?.viewsRestrictions;
    },
  },
});

const isValidCurrency = (currency: string | undefined): boolean => {
  // ISO 4217 currency codes are 3 uppercase letters
  const currencyRegex = /^[A-Z]{3}$/;

  return !!currency && currencyRegex.test(currency);
};

export const hidePaymentOverdueBanner = (): AppThunk => (dispatch) => {
  dispatch(sessionSlice.actions.hidePaymentOverdueBanner());
  SessionAPI.hidePaymentOverdueBanner();
};

export const { login, logout, setupProfile, updateViewsRestrictions } =
  sessionSlice.actions;
export const {
  selectIsSessionRequested,
  selectIsSessionLoading,
  selectIsAuthorized,
  selectEmail,
  selectOrganizationId,
  selectWebhookHash,
  selectShowPaymentOverdueBanner,
  selectCurrency,
  selectIsAdmin,
  selectUserId,
  selectIsDisabled,
  selectViewsRestrictions,
} = sessionSlice.selectors;
